import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  Text,
  Flex,
} from '@chakra-ui/react';
import Button from 'common/components/button';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Input from 'common/components/input';
import SelectInput from 'common/components/select-input';
import FormError from 'common/components/form-error';
import { toDatePickerFormat } from 'common/utils/toDatePickerFormat';
import { schoolLevelOptions } from 'common/utils/selectOptions';
import { studentStatusEnum } from 'common/utils/enums';

const StudentConfirmationModal = ({
  isOpen,
  onClose,
  onConfirmNotStudent,
  onConfirmStudent,
  planData,
}) => {
  const studentOptions = [
    { value: 'not-student', label: "I'm not a student" },
    { value: 'student', label: "I'm a student currently enrolled in school" },
  ];

  const initialValues = {
    studentStatus: '',
    schoolLevel:
      planData?.school?.status === studentStatusEnum.CURRENT_STUDENT
        ? planData?.school?.level || ''
        : '',
    schoolGraduationDate:
      planData?.school?.status === studentStatusEnum.CURRENT_STUDENT
        ? toDatePickerFormat(planData?.school?.graduationDate) || ''
        : '',
    schoolProgramEndDate:
      planData?.school?.status === studentStatusEnum.CURRENT_STUDENT
        ? toDatePickerFormat(planData?.school?.programEndDate) || ''
        : '',
  };

  const validationSchema = Yup.object().shape({
    studentStatus: Yup.string().required('Please select an option'),
    schoolLevel: Yup.string().when('studentStatus', {
      is: 'student',
      then: (schema) => schema.required('Please select your school level'),
      otherwise: (schema) => schema,
    }),
    schoolGraduationDate: Yup.date()
      .nullable()
      .when('studentStatus', {
        is: 'student',
        then: (schema) =>
          schema.required('Please select your expected graduation date'),
        otherwise: (schema) => schema,
      }),
    schoolProgramEndDate: Yup.date()
      .nullable()
      .when('studentStatus', {
        is: 'student',
        then: (schema) =>
          schema.required('Please select when your program ends'),
        otherwise: (schema) => schema,
      }),
  });

  const handleSubmit = (values) => {
    if (values.studentStatus === 'not-student') {
      onConfirmNotStudent();
    } else if (values.studentStatus === 'student') {
      onConfirmStudent(values);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        values,
        isValid,
        dirty,
      }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            size={{ base: 'full', md: 'xl' }}
          >
            <ModalOverlay />
            <ModalContent className='rounded-lg mobile:rounded-none mobile:min-h-screen mobile:flex mobile:flex-col'>
              <ModalHeader className='text-xl font-medium border-b border-gray-200'>
                Please confirm that you are not a student
              </ModalHeader>
              <ModalBody className='pt-4 pb-6 mobile:flex-1 mobile:overflow-y-auto'>
                <Flex className='flex-col gap-6'>
                  <Flex className='flex-col gap-2'>
                    <Text className='text-base'>
                      The community that you selected is primarily student
                      housing.
                    </Text>
                    <Text className='text-base text-gray-500'>
                      Please confirm that you are not a current student. Your
                      plan may be amended or your coverage terminated, without
                      refund, in the event that information provided is found to
                      be false or withheld.
                    </Text>
                  </Flex>

                  <div className='flex flex-col gap-3'>
                    {studentOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`border border-gray-200 rounded-md p-3 ${
                          values.studentStatus === option.value
                            ? 'bg-gray-50'
                            : ''
                        }`}
                      >
                        <label className='flex items-center gap-3 cursor-pointer'>
                          <input
                            type='radio'
                            name='studentStatus'
                            value={option.value}
                            checked={values.studentStatus === option.value}
                            onChange={(e) =>
                              setFieldValue('studentStatus', e.target.value)
                            }
                            className='w-4 h-4'
                          />
                          <span className='text-base'>{option.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>

                  {values.studentStatus === 'student' && (
                    <div className='transition-all duration-300 ease-in-out transform opacity-100 scale-100'>
                      <div className='border-t border-gray-200 pt-4 mt-2'>
                        <Text className='font-medium text-base mb-4 text-gray-700'>
                          School Information
                        </Text>

                        <div className='space-y-4'>
                          <div>
                            <Text className='text-sm font-medium mb-2 text-gray-600'>
                              School Level{' '}
                              <span className='text-red-500'>*</span>
                            </Text>
                            <SelectInput
                              placeholderText='Select your school level'
                              options={schoolLevelOptions}
                              value={schoolLevelOptions.find(
                                (level) => level.value === values.schoolLevel
                              )}
                              onChange={(selectedOption) => {
                                setFieldValue(
                                  'schoolLevel',
                                  selectedOption?.value || ''
                                );
                              }}
                              onBlur={handleBlur}
                              name='schoolLevel'
                            />
                            <FormError name='schoolLevel' />
                          </div>

                          <div>
                            <Text className='text-sm font-medium mb-2 text-gray-600'>
                              Expected Graduation Date{' '}
                              <span className='text-red-500'>*</span>
                            </Text>
                            <Input
                              type='date'
                              name='schoolGraduationDate'
                              value={values.schoolGraduationDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <FormError name='schoolGraduationDate' />
                          </div>

                          <div>
                            <Text className='text-sm font-medium mb-2 text-gray-600'>
                              Expected Program End Date{' '}
                              <span className='text-red-500'>*</span>
                            </Text>
                            <Input
                              type='date'
                              name='schoolProgramEndDate'
                              value={values.schoolProgramEndDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <FormError name='schoolProgramEndDate' />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Flex>
              </ModalBody>
              <ModalFooter className='border-t border-gray-200'>
                <ButtonGroup>
                  <Button
                    title='Cancel'
                    onClick={onClose}
                    wFull={false}
                    theme='secondary'
                  />
                  <Button
                    title='Continue'
                    onClick={handleSubmit}
                    wFull={false}
                    disabled={!isValid || !dirty}
                  />
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default StudentConfirmationModal;
